/*
  Shipping protection "enable by default" - corrected implementation.

  AMP's cart config carries `enableDefault` on the CartShippingProtectionBlock, but their storefront
  script only validates that field and never reads it, so the switch always renders off. This file
  reimplements the setting.

  It deliberately lives outside `amp-upsell.js` and takes over the two AMP callbacks at the bottom,
  so the older copy of this logic inside that file is never reached. Load this AFTER amp-upsell.js.

  Why it exists in this shape: `/cart/add.js` INCREMENTS, and AMP hands us cart snapshots that can
  predate our own add. Trusting those snapshots is what charged shoppers twice - the snapshot said
  "no protection in cart" after we had already added it, so we added a second unit. Everything below
  decides against a freshly fetched cart and repairs a line that is already over quantity 1.

  Shopper intent still wins: the moment protection disappears from a cart that still has products, we
  treat that as an opt-out and never add it back. The flag lives in localStorage on purpose, so a "no"
  survives reloads and new tabs.
*/
(function () {
  const OPT_OUT_KEY = 'boomba_amp_shipping_protection_opt_out';
  const MAX_ADD_ATTEMPTS = 2;

  // The guard has to outlive the response. AMP keeps emitting the pre-add cart for a short while
  // after /cart/add.js resolves, and that stale snapshot is exactly what triggered the double add.
  const COOLDOWN_MS = 2000;

  let protectionWasInCart = false;
  let requestInFlight = false;
  let quietUntil = 0;
  let failedAttempts = 0;
  let configRequest = null;

  function root () {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function readOptOut () {
    try {
      return window.localStorage.getItem(OPT_OUT_KEY) === '1';
    } catch (error) {
      // Private browsing modes throw on storage access. Falling back to "not opted out" keeps the
      // merchant's default, and the worst case is a toggle the shopper has to switch off again.
      return false;
    }
  }

  function writeOptOut () {
    try {
      window.localStorage.setItem(OPT_OUT_KEY, '1');
    } catch (error) {
      console.error('Error:', error);
    }
  }

  function postJson (path, payload) {
    requestInFlight = true;

    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(path + ' failed with status ' + response.status);
        }

        failedAttempts = 0;
        return response.json();
      })
      .catch((error) => {
        // Stop retrying after a couple of failures so a sold-out or deleted variant can't turn every
        // cart update into another request.
        failedAttempts += 1;
        console.error('Error:', error);
        return null;
      })
      .finally(() => {
        requestInFlight = false;
        quietUntil = Date.now() + COOLDOWN_MS;
      });
  }

  function fetchLiveCart () {
    return fetch(root() + 'cart.js', { cache: 'no-store' })
      .then(response => (response.ok ? response.json() : null))
      .catch((error) => {
        console.error('Error:', error);
        return null;
      });
  }

  function readProtectionBlock (cartTarget) {
    if (!cartTarget || !Array.isArray(cartTarget.blocks)) return null;

    const block = cartTarget.blocks.find(item => (
      item.type === 'CartShippingProtectionBlock' && item.enabled
    ));

    if (!block || !block.content || !block.content.enableDefault) return null;

    const productId = String(block.content.productId || '');
    const variantId = String(block.content.variantId || '');

    if (!productId || !variantId) return null;

    return { productId, variantId };
  }

  function summarise (cart, productId) {
    const lines = cart.items.filter(item => String(item.product_id) === productId);

    return {
      quantity: lines.reduce((total, item) => total + item.quantity, 0),
      variantIds: lines.map(item => item.variant_id),
      hasProtectableItems: cart.items.some(item => String(item.product_id) !== productId)
    };
  }

  function setQuantity (variantIds, quantity) {
    const updates = {};

    variantIds.forEach((variantId) => {
      updates[variantId] = quantity;
    });

    return postJson('cart/update.js', { updates });
  }

  function decide (cart, ids) {
    const state = summarise(cart, ids.productId);

    if (state.quantity > 0) {
      protectionWasInCart = true;

      // Nothing left to protect. Clearing this keeps AMP's "empty cart" view honest, and it is our own
      // cleanup rather than a shopper decision, so it must not count as an opt-out. Dropping the flag
      // here is what guarantees that: with it still set, the next decide() after a new product lands
      // would misread our own removal as the shopper switching the toggle off - permanently.
      if (!state.hasProtectableItems) {
        protectionWasInCart = false;

        return setQuantity(state.variantIds, 0);
      }

      // Repair a cart that already went out of sync - exactly one unit on exactly one line.
      // The first line is kept at quantity 1 and every other protection line is zeroed, so a
      // second variant of the product can never survive the clamp.
      if (state.quantity > 1) {
        const updates = {};

        state.variantIds.forEach((variantId, index) => {
          updates[variantId] = index === 0 ? 1 : 0;
        });

        return postJson('cart/update.js', { updates });
      }

      return Promise.resolve();
    }

    // It was in the cart a moment ago and the products are still there, so the shopper switched it off.
    if (protectionWasInCart) {
      protectionWasInCart = false;

      if (state.hasProtectableItems) {
        writeOptOut();
        return Promise.resolve();
      }
    }

    if (readOptOut()) return Promise.resolve();

    // Protection on its own has nothing to protect, and adding to an empty cart would reopen the drawer.
    if (!state.hasProtectableItems) return Promise.resolve();
    if (failedAttempts >= MAX_ADD_ATTEMPTS) return Promise.resolve();

    return postJson('cart/add.js', { items: [{ id: ids.variantId, quantity: 1 }] });
  }

  function sync (cartTarget) {
    const ids = readProtectionBlock(cartTarget);

    if (!ids) return Promise.resolve();
    if (requestInFlight || Date.now() < quietUntil) return Promise.resolve();

    // Claim the guard BEFORE going async. Two AMP events landing inside one /cart.js round-trip
    // must not both get past this point - that overlap is the double-charge race itself.
    requestInFlight = true;

    // Always decide against a freshly fetched cart. AMP's snapshot is the thing that lied.
    return fetchLiveCart()
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return null;
        return decide(cart, ids);
      })
      .finally(() => {
        requestInFlight = false;
      });
  }

  function fetchCartTarget () {
    // AMP_CART_LOADED hands us the cart without the target config, so fetch it from the same public
    // endpoint their own script uses. That request is already in the browser cache by this point.
    if (configRequest) return configRequest;

    const shopDomain = window.Shopify && window.Shopify.shop;

    if (!shopDomain) return Promise.resolve(null);

    configRequest = fetch('https://cdn.useamp.com/api/conversion/merchant_store/targets/cart/' + shopDomain)
      .then(response => (response.ok ? response.json() : null))
      .catch((error) => {
        console.error('Error:', error);
        return null;
      });

    return configRequest;
  }

  window.BOOMBA_SHIPPING_PROTECTION = { sync, fetchCartTarget };

  /*
    Take over AMP's two callbacks. The other handlers still live in amp-upsell.js as globals, so they
    are called by name here (typeof-guarded, in case load order ever flips). amp-upsell.js carries no
    protection logic of its own any more - this module is the only implementation.
  */
  window.AMP_CART_UPDATED = (cart, cartTarget) => {
    if (typeof ampSyncCartCounts === 'function') ampSyncCartCounts(cart.item_count);
    if (typeof ampHideGiftQuantityButtons === 'function') ampHideGiftQuantityButtons();
    if (typeof ampCheckFreeGift === 'function') ampCheckFreeGift(cart, cartTarget);

    sync(cartTarget);
  };

  window.AMP_CART_LOADED = () => {
    fetchCartTarget().then(cartTarget => sync(cartTarget));
  };
}());
